import type { Root } from './prototypeTypes';
import { playerSelectors } from './mediaPlayerSelectors';
import { hasWebKitPictureInPictureEvidence } from './videoPictureInPicture';

type FullscreenContainer = HTMLElement & {
	webkitRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenDocument = Document & {
	webkitFullscreenEnabled?: boolean;
	webkitExitFullscreen?: () => Promise<void> | void;
};

type WebKitFullscreenVideo = HTMLVideoElement & {
	webkitSupportsFullscreen?: boolean;
	webkitEnterFullscreen?: () => void;
};

export interface VideoPresentationControlCapabilities {
	pictureInPicture: boolean;
	fullscreen: boolean;
}

/**
 * Detect stable browser capabilities before the video controls become visible.
 *
 * A fresh detached video deliberately avoids treating a transient mounted-video
 * state (metadata, source swaps, or an active PiP session) as an environment
 * capability. In particular, do not call WebKit's presentation-mode probe on
 * it: Safari only answers reliably after the real media has metadata.
 */
export function videoPresentationControlCapabilities(root: Root, mountedVideo?: HTMLVideoElement): VideoPresentationControlCapabilities {
	const doc = root instanceof Document ? root : root.ownerDocument ?? document;
	if (!doc?.createElement) return { pictureInPicture: false, fullscreen: false };
	const frame = doc.getElementById(playerSelectors.video.container) as FullscreenContainer | null;
	const fullscreenDocument = doc as FullscreenDocument;
	return {
		pictureInPicture: supportsStablePictureInPicture(doc, mountedVideo),
		fullscreen: (doc.fullscreenEnabled !== false && typeof frame?.requestFullscreen === 'function' && typeof doc.exitFullscreen === 'function')
			|| (fullscreenDocument.webkitFullscreenEnabled !== false
				&& typeof frame?.webkitRequestFullscreen === 'function'
				&& typeof fullscreenDocument.webkitExitFullscreen === 'function')
			|| supportsNativeWebKitVideoFullscreen(mountedVideo),
	};
}

/** iPhone exposes fullscreen on the real video rather than the document/container. */
export function supportsNativeWebKitVideoFullscreen(video?: HTMLVideoElement): boolean {
	if (!video || video.readyState < HTMLMediaElement.HAVE_METADATA) return false;
	const webkit = video as WebKitFullscreenVideo;
	return webkit.webkitSupportsFullscreen === true && typeof webkit.webkitEnterFullscreen === 'function';
}

/** Keeps unsupported presentation actions out of the tab order and off-screen UI. */
export function syncVideoPresentationControls(root: Root, mountedVideo?: HTMLVideoElement): VideoPresentationControlCapabilities {
	const doc = root instanceof Document ? root : root.ownerDocument ?? document;
	const capabilities = videoPresentationControlCapabilities(root, mountedVideo);
	setControlVisibility(doc.getElementById('vfPipBtn'), capabilities.pictureInPicture);
	setControlVisibility(doc.getElementById('vfFullscreenBtn'), capabilities.fullscreen);
	return capabilities;
}

function supportsStablePictureInPicture(doc: Document, mountedVideo?: HTMLVideoElement): boolean {
	const webKitPrototype = HTMLVideoElement.prototype as HTMLVideoElement;
	const hasWebKitPresentation = hasWebKitPictureInPictureEvidence(webKitPrototype);
	// Do not expose PiP while a source is still loading. WebKit's probe is only
	// authoritative on the real mounted video after metadata, and Chromium's
	// standard API would otherwise leave a dead control in the loading view.
	// This is intentionally not cached: every source replacement gets a fresh
	// loadedmetadata event and re-evaluates the current element.
	if (!mountedVideo || mountedVideo.readyState < HTMLMediaElement.HAVE_METADATA) return false;

	if (hasWebKitPresentation) {
		const webkit = mountedVideo as HTMLVideoElement & {
			webkitSupportsPresentationMode?: (mode: 'inline' | 'fullscreen' | 'picture-in-picture') => boolean;
			webkitSetPresentationMode?: (mode: 'inline' | 'fullscreen' | 'picture-in-picture') => void;
		};
		// Standalone WebKit is not hard-coded unavailable: current iOS builds
		// report false, while a future build that reports true is usable.
		return typeof webkit.webkitSetPresentationMode === 'function'
			&& webkit.webkitSupportsPresentationMode?.('picture-in-picture') === true;
	}

	const standard = doc.pictureInPictureEnabled === true
		&& typeof HTMLVideoElement.prototype.requestPictureInPicture === 'function';
	if (standard) return true;
	return false;
}

function setControlVisibility(control: HTMLElement | null, visible: boolean): void {
	if (!control) return;
	control.hidden = !visible;
	control.setAttribute('aria-hidden', String(!visible));
	if (visible && control instanceof HTMLButtonElement) {
		control.disabled = false;
		control.removeAttribute('aria-disabled');
	}
}
